import { differenceInMilliseconds, formatDuration, intervalToDuration } from 'date-fns'
import { Check, X } from 'lucide-react'

import { cn } from '@fortem-monorepo/js-utilities'
import { Spinner } from '@fortem-monorepo/react-ui'
import { Separator } from '@fortem-monorepo/react-ui'
import { StreamEvent } from '@fortem-monorepo/web-sdk'

import { useReportStream } from '@/react-query/use-report-stream'

export interface StreamProgressProps {
  title?: string
  stream: ReturnType<typeof useReportStream>
}

export const StreamProgress: React.FC<StreamProgressProps> = ({ title, stream }) => {
  const { state, isRunning, isCompleted, isFailed } = stream

  const hasError = stream.state.events.some((e) => e.error)

  const events = state.events
    ? state.events.reduce(
        (prev, curr) => {
          const _new = { ...prev }

          if (curr.stage_name) {
            _new[curr.stage_name] = curr
          }

          return _new
        },
        {} as Record<string, StreamEvent>,
      )
    : {}

  return (
    <div
      className={cn(
        'border border-indigo-200 transition-all duration-500 p-4 rounded-lg bg-sidebar fade-in animate-in',
        {
          'border-green-300 bg-green-50/50': isCompleted,
          'border-red-300 bg-sidebar': isFailed || hasError,
        },
      )}
    >
      <div className="flex items-center gap-2">
        {title && <h2 className={'text-heading font-medium text-sm'}>{title}</h2>}
      </div>
      <Separator className={'my-2'} />
      {isRunning && !state.events.length && (
        <div className="flex items-center gap-2">
          <Spinner
            size={12}
            className={cn('transition-all opacity-100 shrink-0', {
              'opacity-0': !isRunning,
            })}
          />
          <span className={'text-sm shimmer-text fade-out animate-out'}>Starting report...</span>
        </div>
      )}
      {Object.entries(events).map(([_stageName, event]) => {
        const stageRunning = event.event_type === 'STAGE_STARTED'
        const stageComplete = event.event_type === 'STAGE_COMPLETED'
        const stageFailed = event.event_type === 'STAGE_FAILED'

        return (
          <div key={event.stage_name} className={'space-y-2'}>
            <div className="flex items-center gap-2">
              {stageFailed && <X size={12} className={'text-red-500 shrink-0'} />}
              {stageComplete && <Check size={12} className={'text-green-500 shrink-0'} />}
              {stageRunning && (
                <Spinner
                  size={12}
                  className={cn('transition-all opacity-100 shrink-0!', {
                    'opacity-0': !isRunning,
                  })}
                />
              )}
              <span
                className={cn('text-sm text-heading', {
                  'shimmer-text': stageRunning,
                })}
              >
                {event.stage_name}
              </span>
              {event.message && (
                <span className={'text-xs text-muted-foreground/50'}>{event.message}</span>
              )}
              {event.error && (
                <span className={'text-xs text-muted-foreground/50'}>{event.error}</span>
              )}
            </div>
            {event.messages && event.messages.length > 0 && (
              <ul className={'ml-10 space-y-1 mb-2 list-disc'}>
                {event.messages.map((m) => (
                  <li className={'text-xs text-muted-foreground fade-in animate-in'}>{m}</li>
                ))}
              </ul>
            )}
          </div>
        )
      })}
      {isFailed && (
        <span className={'text-xs'}>{state.events?.[state.events.length - 1]?.error}</span>
      )}
      {isCompleted && (
        <span className={'text-xs text-muted-foreground fade-out animate-out'}>
          Completed in{' '}
          {formatPreciseDuration(
            new Date(state.result?.started_datetime_utc!),
            new Date(state.result?.finished_datetime_utc!),
          )}
        </span>
      )}
    </div>
  )
}

function formatPreciseDuration(start: Date, end: Date) {
  const duration = intervalToDuration({
    start,
    end,
  })

  let formatted = formatDuration(duration)

  const totalSeconds = differenceInMilliseconds(end, start) / 1000

  // only patch when under 1 minute
  if (!duration.seconds) {
    formatted = `${totalSeconds.toFixed(1)} seconds`
  } else if (!duration.hours && !duration.minutes && !Number.isInteger(totalSeconds)) {
    formatted = formatted.replace(/\d+ seconds?/gim, `${totalSeconds.toFixed(1)} seconds`)
  }

  return formatted
}
